import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setActiveWorkspaceId } from "@/lib/api";
import { clearSessionToken } from "@/lib/authToken";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);
  const [workspace, setWorkspace] = useState(null);

  const loadWorkspaces = useCallback(async (currentUser) => {
    try {
      setActiveWorkspaceId(null);
      const { data } = await api.get("/workspaces");
      setWorkspaces(data);
      const own = data.find((w) => w.workspace_id === currentUser.user_id) || data[0];
      setWorkspace(own);
    } catch {
      setWorkspaces([]);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadWorkspaces(data);
    } catch (e) {
      if (e?.response?.status === 401) clearSessionToken();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [loadWorkspaces]);

  useEffect(() => {
    // CRITICAL: returning from OAuth callback — AuthCallback exchanges the session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const switchWorkspace = useCallback((ws) => {
    setActiveWorkspaceId(ws.role === "owner" ? null : ws.workspace_id);
    setWorkspace(ws);
    window.dispatchEvent(new Event("diary-refresh"));
  }, []);

  const applyUser = useCallback(
    async (u) => {
      setUser(u);
      setLoading(false);
      await loadWorkspaces(u);
    },
    [loadWorkspaces],
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setActiveWorkspaceId(null);
    clearSessionToken();
    setUser(null);
    setWorkspace(null);
    window.location.href = "/";
  }, []);

  const savePreferences = useCallback(async (prefs) => {
    const { data } = await api.put("/auth/preferences", prefs);
    setUser((u) => (u ? { ...u, preferences: data } : u));
    return data;
  }, []);

  const setPreferences = useCallback(
    (prefs) => setUser((u) => (u ? { ...u, preferences: prefs } : u)),
    [],
  );

  const role = workspace?.role || "owner";

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        workspaces,
        workspace,
        role,
        canWrite: role === "owner" || role === "limited_edit",
        isOwner: role === "owner",
        switchWorkspace,
        applyUser,
        checkAuth,
        logout,
        savePreferences,
        setPreferences,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
